// Setting → Product → หน้า config ของสินค้าหนึ่งตัว (เต็มหน้า แบ่งเป็นหัวข้อ)
import { Link, useParams } from "react-router";
import { useCampaignCountLabel } from "../api/use-campaign-count";
import { useMemberCountLabel } from "../api/use-member-count";
import { BrandMembersSection } from "../components/brand-members-section";
import { CampaignsSection } from "../components/campaigns-section";
import { FeatureRows } from "../components/feature-rows";
import { MyJobSwitch } from "../components/my-job-switch";
import { PagePicker } from "../components/page-picker";
import { ProductLogo } from "../components/product-logo";
import { SectionCards } from "../components/section-cards";
import { useEnabledBrands, useProducts } from "../api/use-settings";
import { pickColor } from "../model/setting-menus";

export function ProductDetailPage() {
  const { code = "" } = useParams();
  const { data: products, isPending } = useProducts();
  const { byId: enabledBrands } = useEnabledBrands();
  const campLabel = useCampaignCountLabel(code);
  const memberLabel = useMemberCountLabel(code, !!enabledBrands.get(code));

  const p = (products ?? []).find((x) => x.productCode === code);
  const brand = enabledBrands.get(code);
  const enabled = !!brand;

  if (isPending)
    return (
      <div className="panel">
        <p className="muted">กำลังโหลดข้อมูล…</p>
      </div>
    );

  if (!p)
    return (
      <>
        <div className="content-head">
          <div>
            <div className="breadcrumb">
              <Link to="/setting/product">Setting / Product</Link>
            </div>
            <h1>ไม่พบสินค้า</h1>
          </div>
        </div>
        <div className="panel">
          <p className="muted">ไม่พบสินค้ารหัส #{code}</p>
          <Link className="btn-outline inline-btn" to="/setting/product">
            ← กลับไปรายการสินค้า
          </Link>
        </div>
      </>
    );

  return (
    <>
      <div className="content-head">
        <div>
          <div className="breadcrumb">
            <Link to="/setting/product">Setting / Product</Link> /{" "}
            {p.productEngName || p.productCode}
          </div>
          <h1>Product settings</h1>
        </div>
        <Link className="btn-outline inline-btn" to="/setting/product">
          ← กลับ
        </Link>
      </div>

      <div className="cfg-hero panel">
        <ProductLogo
          url={p.productLogoURL}
          letter={(p.productEngName || "?").charAt(0)}
          size="lg"
        />
        <div className="grow">
          <div className="product-name">{p.productEngName || "-"}</div>
          <div className="product-sub">{p.productThName || ""}</div>
          <div className="product-meta">
            <span className="code-chip">#{p.productCode}</span>
            {p.productActive === "Y" ? (
              <span className="badge badge-green">Active</span>
            ) : (
              <span className="badge badge-gray">Inactive</span>
            )}
          </div>
        </div>
        <div className="cfg-hero-kv">
          <div className="kv">
            <span>Brand</span>
            <b>
              {p.brandEngName || "-"} <span className="code-chip">{p.brandCode}</span>
            </b>
          </div>
          <div className="kv">
            <span>Client</span>
            <b>
              {p.clientEngName || "-"} <span className="code-chip">{p.clientCode}</span>
            </b>
          </div>
        </div>
        <MyJobSwitch
          enabled={enabled}
          payload={{
            id: p.productCode,
            name: p.productEngName,
            tagline: `${p.brandEngName || ""} · ${p.clientEngName || ""}`,
            color: pickColor(p.productCode),
            logoUrl: p.productLogoURL,
          }}
        />
      </div>

      <SectionCards
        sections={[
          {
            id: "channels",
            title: "Channels & Inbox",
            desc: "ผูกเพจ Inbox Center และระบบที่เชื่อมต่อ",
            status: enabled ? "พร้อมตั้งค่า" : "เปิด My Job ก่อน",
            body: (
              <>
                <div className="cfg-sub">
                  <h4>Inbox Center (เมนู Message)</h4>
                  {enabled ? (
                    <>
                      <p className="muted small-note">
                        เลือกเพจที่จะล็อกให้แบรนด์นี้ — หน้า Message จะแสดงเฉพาะเพจนั้น
                      </p>
                      <div className="page-picker">
                        <PagePicker brand={brand} />
                      </div>
                    </>
                  ) : (
                    <p className="muted">เปิดสวิตช์ My Job ด้านบนก่อน จึงจะผูกเพจกับแบรนด์นี้ได้</p>
                  )}
                </div>
                <div className="cfg-sub">
                  <h4>ดึงเนื้อหามาแสดงในเมนูไหน</h4>
                  {enabled ? (
                    <>
                      <p className="muted small-note">ปิดไว้ = เมนูนั้นจะไม่ขึ้นในหน้าแบรนด์นี้</p>
                      <FeatureRows brand={brand} />
                    </>
                  ) : (
                    <p className="muted">เปิดสวิตช์ My Job ด้านบนก่อน</p>
                  )}
                </div>
              </>
            ),
          },
          {
            id: "campaigns",
            title: "Campaigns",
            desc: "สร้างแคมเปญของแบรนด์นี้ — ชื่อจะไปแสดงใน dropdown หน้า My Job",
            status: campLabel,
            body: <CampaignsSection brandId={p.productCode} />,
          },
          {
            id: "access",
            title: "ผู้เข้าถึง",
            desc: "กำหนดว่าใครเห็นแบรนด์นี้ใน My Job ได้บ้าง",
            status: memberLabel,
            body: <BrandMembersSection brandId={p.productCode} enabled={enabled} />,
          },
        ]}
      />
    </>
  );
}
